import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, MessageCircle, Phone } from "lucide-react";
import Footer from "../components/Footer";
import ScrollControls from "../components/ScrollControls";
import banner from "../img/banner-1600.jpg";
import logo from "../img/logo.png";
import { BUSINESS_INFO, SITE_URL } from "../config";
import { trackContactClick } from "../utils/analytics";
import { setSeo } from "../utils/seoUtils";

const commonFaqs = [
  {
    question: "Cưng Home có nhận tư vấn sofa theo không gian thực tế không?",
    answer:
      "Có. Bạn có thể gửi ảnh phòng khách, kích thước khu vực đặt sofa và phong cách mong muốn để Cưng Home tư vấn mẫu phù hợp.",
  },
  {
    question: "Có thể chọn màu vải và kích thước riêng không?",
    answer:
      "Có. Cưng Home hỗ trợ chọn màu vải, chất liệu, kích thước và kiểu dáng theo nhu cầu sử dụng của từng gia đình.",
  },
  {
    question: "Cưng Home có giao hàng và lắp đặt tại nhà không?",
    answer:
      "Có. Cưng Home hỗ trợ tư vấn, giao hàng và lắp đặt tại nhà, đặc biệt tại khu vực Hồ Chí Minh và các khu vực có thể phục vụ.",
  },
];

function makePage({
  key,
  path,
  keyword,
  title,
  description,
  h1,
  intro,
  productHref = "/?type=Sofa#products",
  cta = "Xem mẫu phù hợp",
  highlights,
  sectionOne,
  sectionTwo,
  extraSections = [],
  faq,
  faqs,
}) {
  return {
    key,
    path,
    keyword,
    title,
    description,
    h1,
    intro,
    productHref,
    primaryCta: cta,
    highlights,
    sections: [
      {
        heading: sectionOne.heading,
        body: sectionOne.body,
      },
      {
        heading: sectionTwo.heading,
        body: sectionTwo.body,
      },
      ...extraSections,
    ],
    faqs: faqs || (faq ? [faq, ...commonFaqs] : commonFaqs),
  };
}

export const SEO_LANDING_PAGES = {
  "chinh-sach-thanh-toan-dat-coc": makePage({
    key: "chinh-sach-thanh-toan-dat-coc",
    path: "/chinh-sach-thanh-toan-dat-coc",
    keyword: "chính sách thanh toán đặt cọc Cưng Home",
    title: "Chính Sách Thanh Toán Và Đặt Cọc - Cưng Home",
    description:
      "Chính sách thanh toán và đặt cọc tại Cưng Home khi đặt sofa may đo, giường bọc nệm và nội thất theo yêu cầu: cọc 30%, gửi đơn xác nhận, thanh toán còn lại và lưu thông tin giao dịch.",
    h1: "Chính sách thanh toán và đặt cọc",
    intro:
      "Cưng Home áp dụng quy trình thanh toán rõ ràng cho các đơn hàng sofa may đo, giường bọc nệm và nội thất theo yêu cầu. Sau khi thống nhất mẫu, kích thước, chất liệu, màu sắc và chi phí, Cưng Home gửi đơn xác nhận để khách hàng kiểm tra trước khi tiến hành sản xuất.",
    productHref: "/#products",
    cta: "Xem sản phẩm Cưng Home",
    highlights: [
      "Cưng Home tư vấn và thống nhất các thông tin chính của đơn hàng trước khi sản xuất.",
      "Thông thường khoản đặt cọc là 30% giá trị đơn hàng, được thể hiện trong báo giá hoặc nội dung trao đổi.",
      "Đơn xác nhận thể hiện các thông tin như mẫu, kích thước, chất liệu, màu sắc, chi phí và thời gian giao dự kiến.",
      "Cưng Home chỉ tiến hành chuẩn bị vật tư và sản xuất sau khi khách hàng đã xác nhận nội dung đơn hàng.",
      "Phần thanh toán còn lại được thực hiện theo thỏa thuận khi giao hàng, lắp đặt hoặc bàn giao sản phẩm.",
      "Khách hàng nên lưu lại hóa đơn, biên nhận chuyển khoản hoặc nội dung xác nhận để đối chiếu khi cần.",
    ],
    sectionOne: {
      heading: "Đặt cọc và đơn xác nhận trước khi sản xuất",
      body:
        "Với các sản phẩm may đo, Cưng Home cần chốt rõ mẫu, kích thước, chất liệu, màu vải, thời gian giao dự kiến và chi phí trước khi tiến hành. Sau khi các thông tin chính đã được thống nhất, Cưng Home gửi đơn xác nhận để khách hàng kiểm tra lại. Khi khách hàng xác nhận nội dung đơn, Cưng Home mới bắt đầu chuẩn bị vật tư và sản xuất theo đúng thông tin đã chốt.",
    },
    sectionTwo: {
      heading: "Thanh toán minh bạch, dễ đối chiếu",
      body:
        "Khách hàng có thể thanh toán bằng chuyển khoản hoặc hình thức được hai bên thống nhất. Nội dung thanh toán nên ghi rõ tên khách hàng, số điện thoại hoặc mã đơn nếu có. Cưng Home khuyến khích khách hàng kiểm tra kỹ thông tin người nhận và lưu lại chứng từ giao dịch.",
    },
    faqs: [
      {
        question: "Khi nào cần đặt cọc đơn hàng?",
        answer:
          "Thông thường khách hàng đặt cọc sau khi đã thống nhất mẫu, kích thước, chất liệu, màu sắc, chi phí và thời gian giao dự kiến với Cưng Home. Khoản đặt cọc giúp xác nhận nhu cầu đặt hàng và giữ lịch sản xuất cho sản phẩm may đo.",
      },
      {
        question: "Khoản đặt cọc là bao nhiêu?",
        answer:
          "Thông thường khoản đặt cọc là 30% giá trị đơn hàng. Tỷ lệ hoặc số tiền cụ thể sẽ được thể hiện trong báo giá, đơn xác nhận hoặc nội dung trao đổi trước khi khách hàng thanh toán.",
      },
      {
        question: "Đơn xác nhận được gửi khi nào?",
        answer:
          "Sau khi khách hàng và Cưng Home đã thống nhất các thông tin chính của đơn hàng, Cưng Home gửi đơn xác nhận để khách kiểm tra lại mẫu, kích thước, chất liệu, màu sắc, chi phí và thời gian giao dự kiến trước khi sản xuất.",
      },
      {
        question: "Khi nào Cưng Home bắt đầu làm sản phẩm?",
        answer:
          "Cưng Home chỉ bắt đầu chuẩn bị vật tư và sản xuất sau khi khách hàng đã kiểm tra và xác nhận nội dung đơn hàng. Bước này giúp hạn chế sai lệch về kích thước, màu sắc, chất liệu hoặc thông tin giao nhận.",
      },
      {
        question: "Phần còn lại thanh toán khi nào?",
        answer:
          "Phần còn lại được thanh toán theo thỏa thuận khi giao hàng, lắp đặt hoặc bàn giao sản phẩm, tùy điều kiện cụ thể của từng đơn hàng.",
      },
      {
        question: "Cần lưu lại thông tin gì sau khi chuyển khoản?",
        answer:
          "Khách hàng nên lưu lại biên nhận chuyển khoản, nội dung giao dịch, báo giá hoặc tin nhắn xác nhận đơn hàng để đối chiếu khi cần.",
      },
    ],
  }),
  "ve-cung-home": makePage({
    key: "ve-cung-home",
    path: "/ve-cung-home",
    keyword: "về Cưng Home",
    title: "Về Cưng Home - Sofa May Đo Và Giường Bọc Nệm",
    description:
      "Tìm hiểu Cưng Home: thương hiệu sofa may đo, giường bọc nệm và nội thất phòng khách tại TP.HCM, văn phòng tư vấn S501 Vinhomes Grand Park và khu vực xưởng Long Bình.",
    h1: "Về Cưng Home",
    intro:
      "Cưng Home tư vấn sofa may đo, giường bọc nệm và nội thất phòng khách theo nhu cầu sử dụng thực tế của từng gia đình, từ kích thước, màu vải đến cảm giác ngồi và phong cách không gian.",
    productHref: "/#products",
    cta: "Xem sản phẩm Cưng Home",
    highlights: [
      `${BUSINESS_INFO.officeLabel}: ${BUSINESS_INFO.addressShort}.`,
      `${BUSINESS_INFO.workshopLabel}: ${BUSINESS_INFO.workshopAreaShort}.`,
      "Tư vấn chọn form, kích thước, màu vải và chất liệu theo không gian thực tế.",
      "Bảo hành 3 năm (36 tháng) cho khung sườn và nệm mousse theo chính sách.",
    ],
    sectionOne: {
      heading: "Cưng Home làm sofa theo đúng không gian sống",
      body:
        "Mỗi phòng khách có diện tích, ánh sáng, lối đi và thói quen sử dụng khác nhau. Vì vậy Cưng Home ưu tiên tư vấn từ nhu cầu thật: khách gửi ảnh không gian, kích thước khu vực đặt sofa, màu nội thất hiện có và phong cách mong muốn để chọn phương án phù hợp.",
    },
    sectionTwo: {
      heading: "Quy trình tư vấn rõ ràng, dễ kiểm tra",
      body:
        "Khách hàng có thể trao đổi trước qua Zalo, Fanpage hoặc điện thoại. Cưng Home tư vấn mẫu, chất liệu, kích thước, phương án giao lắp và chính sách bảo hành trước khi chốt đơn. Văn phòng tư vấn đặt tại S501 Vinhomes Grand Park, khu vực xưởng ở Long Bình gần Vinhomes Grand Park khoảng 1km.",
    },
    faq: {
      question: "S501 là văn phòng hay xưởng?",
      answer:
        "Không. S501 Vinhomes Grand Park là văn phòng tư vấn và điểm liên hệ. Khu vực xưởng của Cưng Home ở Long Bình, gần Vinhomes Grand Park khoảng 1km.",
    },
  }),
  sofa: makePage({
    key: "sofa",
    path: "/sofa",
    keyword: "sofa",
    title: "Sofa Đẹp May Đo Theo Yêu Cầu - Cưng Home",
    description:
      "Cưng Home cung cấp sofa đẹp, sofa phòng khách, sofa chữ L, sofa bed và ghế bọc nệm may đo theo kích thước, chất liệu, màu sắc riêng.",
    h1: "Sofa đẹp may đo cho phòng khách",
    intro:
      "Cưng Home thiết kế và hoàn thiện sofa theo nhu cầu sử dụng thực tế: kích thước căn hộ, phong cách nội thất, chất liệu vải và ngân sách của từng gia đình.",
    highlights: [
      "Sofa băng, sofa chữ L, sofa bed, ghế đơn và đôn phối bộ.",
      "Nhiều lựa chọn vải: bố nhung, nhung, nỉ, lông cừu và các dòng vải dễ vệ sinh.",
      "Tư vấn kích thước, màu sắc, chất liệu và bố cục theo không gian phòng khách.",
    ],
    sectionOne: {
      heading: "Chọn sofa theo đúng không gian",
      body:
        "Một bộ sofa đẹp không chỉ nằm ở kiểu dáng. Kích thước, độ sâu ngồi, chiều cao tay vịn và màu vải cần phù hợp với diện tích phòng khách, thói quen sinh hoạt và ánh sáng trong nhà.",
    },
    sectionTwo: {
      heading: "Sofa may đo linh hoạt",
      body:
        "Khách hàng có thể chọn form sofa, đổi màu vải, tinh chỉnh kích thước và thêm đôn hoặc ghế dài để bộ sofa vừa vặn hơn với căn hộ, nhà phố hoặc studio.",
    },
    faq: {
      question: "Cưng Home có những loại sofa nào?",
      answer:
        "Cưng Home có sofa băng, sofa chữ L, sofa bed, ghế đơn, đôn và các bộ sofa phối theo không gian phòng khách.",
    },
  }),
  "sofa-dep": makePage({
    key: "sofa-dep",
    path: "/sofa-dep",
    keyword: "sofa đẹp",
    title: "Sofa Đẹp Cho Phòng Khách - Cưng Home",
    description:
      "Tham khảo sofa đẹp cho phòng khách tại Cưng Home: sofa chữ L, sofa băng, sofa bed, ghế bọc nệm với màu vải và kích thước tùy chỉnh.",
    h1: "Sofa đẹp cho phòng khách hiện đại",
    intro:
      "Các mẫu sofa tại Cưng Home hướng tới cảm giác êm, form dáng gọn và dễ phối với căn hộ, nhà phố hoặc không gian phòng khách gia đình.",
    highlights: [
      "Form sofa hiện đại, dễ phối với nhiều phong cách nội thất.",
      "Tùy chọn màu vải trung tính, sáng, trầm hoặc nổi bật theo sở thích.",
      "Có thể tư vấn theo ảnh phòng khách và kích thước thực tế.",
    ],
    sectionOne: {
      heading: "Sofa đẹp cần vừa mắt và vừa nhà",
      body:
        "Một mẫu sofa đẹp nên cân bằng giữa kiểu dáng, kích thước, màu sắc và độ thoải mái. Cưng Home tư vấn để bộ sofa không chiếm quá nhiều diện tích nhưng vẫn đủ chỗ ngồi cho sinh hoạt hằng ngày.",
    },
    sectionTwo: {
      heading: "Chất liệu ảnh hưởng lớn đến cảm giác sử dụng",
      body:
        "Tùy nhu cầu, khách có thể chọn vải mềm, vải dễ vệ sinh, nhung, nỉ hoặc bố nhung. Màu vải được cân nhắc theo ánh sáng, sàn nhà và màu tường.",
    },
    faq: {
      question: "Nên chọn màu sofa nào để phòng khách đẹp hơn?",
      answer:
        "Các màu trung tính như kem, xám, be, nâu nhạt thường dễ phối. Nếu phòng nhiều ánh sáng, có thể chọn màu trầm hơn để tạo điểm nhấn.",
    },
  }),
  "sofa-phong-khach": makePage({
    key: "sofa-phong-khach",
    path: "/sofa-phong-khach",
    keyword: "sofa phòng khách",
    title: "Sofa Phòng Khách May Đo Theo Không Gian - Cưng Home",
    description:
      "Cưng Home tư vấn sofa phòng khách cho căn hộ, nhà phố và không gian gia đình với kích thước, kiểu dáng, chất liệu tùy chỉnh.",
    h1: "Sofa phòng khách theo đúng không gian",
    intro:
      "Sofa phòng khách cần phù hợp diện tích, lối đi, ánh sáng và thói quen sinh hoạt. Cưng Home hỗ trợ chọn kiểu sofa vừa đẹp vừa tiện dùng.",
    highlights: [
      "Tư vấn sofa cho căn hộ, nhà phố, phòng khách nhỏ hoặc rộng.",
      "Có sofa băng, sofa chữ L, sofa cong, sofa bed và đôn rời.",
      "Gợi ý kích thước dựa trên chiều dài tường và bố cục nội thất.",
    ],
    sectionOne: {
      heading: "Bố cục quyết định kiểu sofa",
      body:
        "Phòng khách dài có thể hợp sofa băng hoặc sofa chữ L gọn. Phòng rộng có thể phối thêm đôn, ghế đơn hoặc module để tạo khu tiếp khách thoải mái hơn.",
    },
    sectionTwo: {
      heading: "Chọn sofa theo cách gia đình sử dụng",
      body:
        "Nếu gia đình thường xem phim, tiếp khách hoặc nằm nghỉ, độ sâu ngồi và độ mềm nệm rất quan trọng. Cưng Home tư vấn theo thói quen thay vì chỉ theo ảnh mẫu.",
    },
    faq: {
      question: "Phòng khách nhỏ nên chọn sofa gì?",
      answer:
        "Phòng khách nhỏ thường hợp sofa băng, sofa chữ L gọn hoặc sofa kèm đôn rời để dễ thay đổi bố cục khi cần.",
    },
  }),
  "sofa-chu-l": makePage({
    key: "sofa-chu-l",
    path: "/sofa-chu-l",
    keyword: "sofa chữ L",
    title: "Sofa Chữ L Cho Căn Hộ Và Phòng Khách - Cưng Home",
    description:
      "Cưng Home tư vấn sofa chữ L, sofa góc và sofa chữ L may đo kích thước, màu vải, chất liệu theo không gian phòng khách.",
    h1: "Sofa chữ L cho căn hộ và phòng khách",
    intro:
      "Sofa chữ L giúp tận dụng góc phòng, tăng chỗ ngồi và tạo cảm giác phòng khách gọn hơn nếu chọn đúng kích thước.",
    productHref: "/?type=Sofa L#products",
    highlights: [
      "Phù hợp căn hộ, phòng khách vừa và rộng.",
      "Có thể chọn chiều góc trái hoặc phải theo mặt bằng.",
      "Tùy chỉnh chiều dài, chiều sâu, màu vải và đôn phối thêm.",
    ],
    sectionOne: {
      heading: "Sofa chữ L cần đúng chiều góc",
      body:
        "Trước khi chọn sofa chữ L, cần xác định lối đi, vị trí tivi, cửa ban công và hướng mở góc. Cưng Home tư vấn để sofa không chắn luồng di chuyển trong nhà.",
    },
    sectionTwo: {
      heading: "May đo giúp sofa chữ L vừa vặn hơn",
      body:
        "Với sofa chữ L, sai lệch kích thước nhỏ cũng có thể làm phòng khách chật. May đo theo không gian giúp bộ sofa cân đối hơn với tường, thảm, bàn trà và kệ tivi.",
    },
    faq: {
      question: "Sofa chữ L có đổi chiều góc được không?",
      answer:
        "Tùy mẫu. Một số mẫu có thể thiết kế theo góc trái hoặc góc phải ngay từ đầu để phù hợp mặt bằng phòng khách.",
    },
  }),
  "sofa-bed": makePage({
    key: "sofa-bed",
    path: "/sofa-bed",
    keyword: "sofa bed",
    title: "Sofa Bed Và Sofa Giường Cho Nhà Nhỏ - Cưng Home",
    description:
      "Cưng Home tư vấn sofa bed, sofa giường cho căn hộ nhỏ, phòng khách, phòng làm việc hoặc không gian cần thêm chỗ nghỉ linh hoạt.",
    h1: "Sofa bed tiện dụng cho nhà nhỏ",
    intro:
      "Sofa bed phù hợp khách cần một món nội thất linh hoạt: ban ngày dùng tiếp khách, khi cần có thể biến thành chỗ nghỉ hoặc giường phụ.",
    productHref: "/?type=Sofa Bed#products",
    highlights: [
      "Phù hợp căn hộ nhỏ, studio, phòng làm việc hoặc phòng khách đa năng.",
      "Tư vấn kích thước mở ra để không vướng lối đi.",
      "Có thể chọn màu vải và độ mềm phù hợp nhu cầu sử dụng.",
    ],
    sectionOne: {
      heading: "Sofa bed cần tính cả kích thước khi mở",
      body:
        "Khi chọn sofa bed, không chỉ đo kích thước lúc gấp mà cần tính cả chiều dài khi mở thành giường để vẫn còn lối đi và khoảng trống sử dụng.",
    },
    sectionTwo: {
      heading: "Phù hợp không gian đa năng",
      body:
        "Sofa bed là lựa chọn tốt cho nhà nhỏ, căn hộ cho thuê hoặc phòng làm việc có nhu cầu nghỉ ngơi. Cưng Home tư vấn mẫu theo tần suất sử dụng thực tế.",
    },
    faq: {
      question: "Sofa bed có phù hợp dùng hằng ngày không?",
      answer:
        "Tùy mẫu và nhu cầu. Nếu dùng thường xuyên, nên ưu tiên khung chắc, nệm đủ nâng đỡ và kích thước mở ra phù hợp không gian.",
    },
  }),
  "sofa-tu-xuong": makePage({
    key: "sofa-tu-xuong",
    path: "/sofa-tu-xuong",
    keyword: "sofa từ xưởng",
    title: "Sofa Từ Xưởng - May Đo Theo Yêu Cầu Tại Cưng Home",
    description:
      "Đặt sofa từ xưởng Cưng Home với kích thước, màu vải, chất liệu và kiểu dáng tùy chỉnh. Tư vấn thiết kế, giao hàng và lắp đặt tại nhà.",
    h1: "Sofa từ xưởng may đo theo yêu cầu",
    intro:
      "Cưng Home nhận tư vấn và sản xuất sofa theo đơn đặt hàng, giúp khách chủ động hơn về kích thước, màu sắc, chất liệu và chi phí.",
    productHref: "/?type=Sofa Xưởng#products",
    highlights: [
      "Chủ động chọn form dáng, màu vải, độ mềm và kích thước.",
      "Phù hợp khách cần sofa riêng cho căn hộ, nhà phố, homestay hoặc showroom.",
      "Đội ngũ tư vấn hỗ trợ chọn mẫu theo ảnh tham khảo và mặt bằng thực tế.",
    ],
    sectionOne: {
      heading: "Lợi thế khi đặt sofa từ xưởng",
      body:
        "Sofa từ xưởng giúp bạn không bị giới hạn bởi kích thước có sẵn. Bộ sofa có thể được điều chỉnh theo chiều dài tường, vị trí lối đi, thang máy và nhu cầu sử dụng hằng ngày.",
    },
    sectionTwo: {
      heading: "Quy trình tư vấn rõ ràng",
      body:
        "Cưng Home tiếp nhận nhu cầu, tư vấn mẫu phù hợp, chốt kích thước và chất liệu trước khi sản xuất. Khách hàng có thể trao đổi trực tiếp qua Zalo hoặc Fanpage.",
    },
    faq: {
      question: "Sofa từ xưởng có ưu điểm gì?",
      answer:
        "Sofa từ xưởng giúp chủ động kích thước, chất liệu, màu sắc và form dáng, phù hợp với khách muốn bộ sofa vừa đúng không gian.",
    },
  }),
  "xuong-sofa-tphcm": makePage({
    key: "xuong-sofa-tphcm",
    path: "/xuong-sofa-tphcm",
    keyword: "xưởng sofa TPHCM",
    title: "Xưởng Sofa TPHCM - Sofa May Đo Tại Cưng Home",
    description:
      "Tìm xưởng sofa TPHCM? Cưng Home tư vấn sofa may đo, sofa phòng khách, sofa chữ L, sofa bed với giao hàng và lắp đặt tại nhà.",
    h1: "Xưởng sofa TPHCM tư vấn may đo",
    intro:
      "Cưng Home hỗ trợ khách tại Hồ Chí Minh chọn mẫu sofa, chốt kích thước, chất liệu, màu vải và phương án giao lắp phù hợp.",
    productHref: "/?type=Sofa Xưởng#products",
    highlights: [
      "Tư vấn sofa theo kích thước căn hộ và nhà phố tại Hồ Chí Minh.",
      "Hỗ trợ chọn chất liệu, màu vải và form sofa theo ảnh tham khảo.",
      "Liên hệ nhanh qua Zalo, Messenger hoặc điện thoại.",
    ],
    sectionOne: {
      heading: "Xưởng sofa cần rõ quy trình tư vấn",
      body:
        "Khi đặt sofa theo yêu cầu, khách cần được tư vấn về kích thước, chất liệu, độ mềm và thời gian hoàn thiện. Cưng Home tập trung vào trao đổi rõ trước khi chốt phương án.",
    },
    sectionTwo: {
      heading: "Phù hợp khách cần sofa riêng",
      body:
        "Nếu sofa bán sẵn không vừa không gian, phương án may đo giúp tối ưu chiều dài, chiều sâu, màu vải và phong cách tổng thể của phòng khách.",
    },
    faq: {
      question: "Cưng Home có phục vụ khách tại TPHCM không?",
      answer:
        "Có. Cưng Home tư vấn và hỗ trợ giao lắp tại Hồ Chí Minh tùy khu vực, với liên hệ nhanh qua Zalo hoặc Messenger.",
    },
  }),
  "sofa-vinhomes-grand-park": makePage({
    key: "sofa-vinhomes-grand-park",
    path: "/sofa-vinhomes-grand-park",
    keyword: "sofa Vinhomes Grand Park",
    title: "Sofa Vinhomes Grand Park - Cưng Home",
    description:
      "Cưng Home tư vấn sofa Vinhomes Grand Park: sofa phòng khách, sofa chữ L, sofa bed và sofa may đo theo kích thước căn hộ, giao lắp tại nhà.",
    h1: "Sofa cho căn hộ Vinhomes Grand Park",
    intro:
      "Cưng Home hỗ trợ cư dân Vinhomes Grand Park chọn sofa theo diện tích căn hộ, bố cục phòng khách, màu nội thất và nhu cầu sử dụng hằng ngày.",
    productHref: "/?type=Sofa#products",
    highlights: [
      `${BUSINESS_INFO.officeLabel}: ${BUSINESS_INFO.addressShort}.`,
      `${BUSINESS_INFO.workshopLabel}: ${BUSINESS_INFO.workshopAreaShort}.`,
      "Tư vấn sofa băng, sofa chữ L, sofa bed và sofa may đo theo mặt bằng căn hộ.",
      "Hỗ trợ chọn chất liệu, màu vải, kích thước và phương án giao lắp tại nhà.",
    ],
    sectionOne: {
      heading: "Tư vấn sofa theo mặt bằng căn hộ",
      body:
        "Căn hộ tại Vinhomes Grand Park thường cần sofa vừa đủ chỗ ngồi nhưng vẫn giữ lối đi thoáng. Cưng Home tư vấn theo chiều dài tường, vị trí ban công, kệ tivi và thói quen sử dụng để chọn sofa băng, sofa chữ L hoặc sofa bed phù hợp.",
    },
    sectionTwo: {
      heading: "Liên hệ nhanh trước khi xem mẫu",
      body:
        "Khách hàng có thể gửi ảnh phòng khách, kích thước khu vực đặt sofa và phong cách mong muốn qua Zalo hoặc Fanpage. Văn phòng tư vấn của Cưng Home ở S501 Vinhomes Grand Park; khu vực xưởng ở Long Bình gần Vinhomes Grand Park khoảng 1km.",
    },
    faq: {
      question: "Cưng Home có tư vấn sofa cho cư dân Vinhomes Grand Park không?",
      answer:
        `Có. Cưng Home có ${BUSINESS_INFO.officeLabel.toLowerCase()} tại ${BUSINESS_INFO.addressShort} và hỗ trợ tư vấn sofa theo kích thước căn hộ, màu vải, chất liệu và nhu cầu sử dụng của cư dân Vinhomes Grand Park.`,
    },
  }),
  "sofa-thu-duc": makePage({
    key: "sofa-thu-duc",
    path: "/sofa-thu-duc",
    keyword: "sofa Thủ Đức",
    title: "Sofa Thủ Đức - Sofa May Đo Tại Cưng Home",
    description:
      "Tìm sofa Thủ Đức? Cưng Home tư vấn sofa phòng khách, sofa chữ L, sofa bed và sofa may đo, hỗ trợ giao lắp tại khu vực TP. Thủ Đức.",
    h1: "Sofa Thủ Đức tư vấn theo không gian",
    intro:
      "Cưng Home hỗ trợ khách tại TP. Thủ Đức chọn sofa phù hợp căn hộ, nhà phố và phòng khách gia đình, từ kích thước, chất liệu đến màu vải.",
    productHref: "/?type=Sofa#products",
    highlights: [
      "Tư vấn sofa cho căn hộ, nhà phố và phòng khách tại khu vực TP. Thủ Đức.",
      "Có sofa băng, sofa chữ L, sofa bed và các mẫu sofa may đo theo yêu cầu.",
      `${BUSINESS_INFO.workshopLabel}: ${BUSINESS_INFO.workshopArea}.`,
      "Trao đổi nhanh qua Zalo, Messenger hoặc điện thoại trước khi chốt phương án.",
    ],
    sectionOne: {
      heading: "Chọn sofa theo diện tích và lối đi",
      body:
        "Nhà phố và căn hộ tại Thủ Đức có nhiều kiểu mặt bằng khác nhau. Cưng Home tư vấn kích thước sofa theo chiều dài tường, thang máy, lối vào, vị trí cửa và thói quen sử dụng để bộ sofa vừa đẹp vừa thuận tiện.",
    },
    sectionTwo: {
      heading: "Sofa may đo cho nhu cầu sử dụng thật",
      body:
        "Nếu sofa bán sẵn không vừa kích thước hoặc không đúng màu vải mong muốn, phương án may đo giúp khách chủ động hơn về form dáng, chất liệu, độ mềm và bố cục tổng thể. Cưng Home hỗ trợ tư vấn online trước khi giao lắp tại nhà tùy khu vực.",
    },
    faq: {
      question: "Cưng Home có giao lắp sofa tại Thủ Đức không?",
      answer:
        "Có. Cưng Home hỗ trợ tư vấn, giao hàng và lắp đặt sofa tại khu vực TP. Thủ Đức tùy lịch và điều kiện thực tế của từng đơn hàng.",
    },
  }),
  "sofa-quan-9": makePage({
    key: "sofa-quan-9",
    path: "/sofa-quan-9",
    keyword: "sofa Quận 9",
    title: "Sofa Quận 9 May Đo Theo Không Gian | Cưng Home Thủ Đức",
    description:
      "Cưng Home nhận làm sofa may đo tại Quận 9, TP. Thủ Đức: tùy chỉnh kích thước, màu vải và chất liệu. Xưởng gần Vinhomes Grand Park, giao lắp tận nhà.",
    h1: "Sofa Quận 9 may đo theo phòng khách",
    intro:
      "Cưng Home tư vấn sofa cho căn hộ và nhà phố tại khu vực Quận 9 cũ, nay thuộc TP. Thủ Đức. Khách hàng có thể gửi ảnh phòng, kích thước và mẫu yêu thích để được tư vấn form ghế, màu vải và phương án giao lắp phù hợp.",
    productHref: "/?type=Sofa#products",
    cta: "Xem các mẫu sofa",
    highlights: [
      "Khu vực xưởng tại Long Bình, gần Vinhomes Grand Park khoảng 1km.",
      "Nhận may đo sofa băng, sofa chữ L và sofa bed theo kích thước phòng.",
      "Phục vụ Long Bình, Long Thạnh Mỹ, Phước Long, Tăng Nhơn Phú và các phường lân cận.",
      "Tùy chọn form dáng, độ sâu ngồi, màu vải, chất liệu và độ êm.",
      "Xác nhận cấu hình, chi phí và thời gian dự kiến trước khi sản xuất.",
      "Bảo hành khung sườn và nệm mousse 3 năm theo chính sách Cưng Home.",
    ],
    sectionOne: {
      heading: "Chọn sofa phù hợp căn hộ và nhà phố Quận 9",
      body:
        "Mặt bằng tại Quận 9 có nhiều loại, từ căn hộ một đến ba phòng ngủ tại Vinhomes Grand Park, nhà phố Long Bình đến các không gian sinh hoạt rộng ở Long Thạnh Mỹ. Cưng Home dựa trên chiều dài tường, vị trí cửa, ban công, kệ tivi và lối đi để tư vấn sofa băng, sofa chữ L hoặc sofa bed. Kích thước được chốt theo nhu cầu ngồi thực tế, tránh bộ ghế quá lớn làm phòng chật hoặc quá nhỏ so với không gian.",
    },
    sectionTwo: {
      heading: "May đo kích thước, màu vải và cảm giác ngồi",
      body:
        "Khách hàng không bị giới hạn ở kích thước và màu có sẵn. Tùy từng mẫu, Cưng Home hỗ trợ điều chỉnh chiều dài, chiều sâu, tay vịn, hướng góc L, màu và chất liệu bọc. Gia đình có trẻ nhỏ hoặc thú cưng có thể trao đổi nhu cầu vệ sinh và độ bền; căn hộ phong cách Japandi, tối giản hoặc hiện đại có thể chọn bảng màu và bề mặt vải phù hợp với sàn, rèm và ánh sáng trong phòng.",
    },
    extraSections: [
      {
        heading: "Quy trình đặt sofa tại khu vực Quận 9",
        body:
          "Khách gửi ảnh và kích thước khu vực đặt sofa qua Zalo hoặc Fanpage. Cưng Home trao đổi mẫu, cấu hình, chất liệu và ngân sách; sau đó xác nhận báo giá, kích thước, màu sắc và thời gian dự kiến trước khi sản xuất. Khi sản phẩm hoàn thiện, hai bên hẹn lịch giao nhận và lắp đặt phù hợp với quy định ra vào, thang máy và khung giờ của căn hộ hoặc khu dân cư.",
      },
      {
        heading: "Xưởng gần Vinhomes Grand Park, thuận tiện tư vấn",
        body:
          "Văn phòng tư vấn của Cưng Home tại S501 Vinhomes Grand Park và khu vực xưởng ở Long Bình, gần Vinhomes Grand Park khoảng 1km. Vị trí này thuận tiện để hỗ trợ khách tại Quận 9 cũ và TP. Thủ Đức. Trước khi đến, khách nên liên hệ để được xác nhận lịch tư vấn và chuẩn bị đúng mẫu vải hoặc sản phẩm đang quan tâm.",
      },
      {
        heading: "Giá sofa được xác định như thế nào?",
        body:
          "Chi phí phụ thuộc vào form ghế, kích thước, vật liệu khung, loại nệm, chất liệu bọc và các chi tiết hoàn thiện. Vì sofa được may đo theo từng không gian, Cưng Home báo giá sau khi đã nắm kích thước và cấu hình mong muốn. Báo giá và thông tin sản phẩm được xác nhận trước khi khách đặt cọc và xưởng bắt đầu sản xuất.",
      },
    ],
    faqs: [
      {
        question: "Cưng Home có giao lắp sofa tại Quận 9 không?",
        answer:
          "Có. Cưng Home hỗ trợ giao và lắp đặt tại khu vực Quận 9 cũ, nay thuộc TP. Thủ Đức, theo lịch và điều kiện thực tế của từng đơn hàng.",
      },
      {
        question: "Có thể đặt sofa theo kích thước phòng khách không?",
        answer:
          "Có. Khách có thể gửi chiều dài tường, ảnh phòng và vị trí cửa hoặc lối đi để Cưng Home tư vấn kích thước, kiểu sofa và hướng góc phù hợp.",
      },
      {
        question: "Xưởng Cưng Home ở đâu?",
        answer:
          "Khu vực xưởng ở Long Bình, TP. Thủ Đức, gần Vinhomes Grand Park khoảng 1km. Văn phòng tư vấn tại S501 Vinhomes Grand Park. Khách nên liên hệ trước khi đến.",
      },
      {
        question: "Sofa may đo được bảo hành bao lâu?",
        answer:
          "Cưng Home áp dụng bảo hành 3 năm (36 tháng) cho khung sườn và nệm mousse theo chính sách và tình trạng cụ thể của sản phẩm.",
      },
      {
        question: "Tôi cần gửi thông tin gì để nhận báo giá?",
        answer:
          "Bạn nên gửi mẫu sofa yêu thích, ảnh phòng khách, chiều dài khu vực đặt ghế, số chỗ ngồi, màu và chất liệu mong muốn. Các thông tin này giúp báo giá sát nhu cầu hơn.",
      },
    ],
  }),
  "sofa-may-do": makePage({
    key: "sofa-may-do",
    path: "/sofa-may-do",
    keyword: "sofa may đo",
    title: "Sofa May Đo Theo Kích Thước Và Màu Vải - Cưng Home",
    description:
      "Đặt sofa may đo tại Cưng Home theo kích thước phòng khách, kiểu dáng, màu vải, chất liệu và nhu cầu sử dụng riêng.",
    h1: "Sofa may đo theo kích thước riêng",
    intro:
      "Sofa may đo phù hợp khi bạn cần bộ sofa vừa đúng tường, đúng phong cách và đúng thói quen sử dụng của gia đình.",
    productHref: "/?type=Sofa Xưởng#products",
    highlights: [
      "Chọn kích thước theo mặt bằng thực tế.",
      "Tùy chỉnh màu vải, chất liệu, độ mềm và kiểu tay vịn.",
      "Tư vấn phối sofa với bàn trà, thảm, kệ tivi và ánh sáng phòng.",
    ],
    sectionOne: {
      heading: "May đo giúp tránh lãng phí không gian",
      body:
        "Sofa quá lớn làm phòng khách chật, sofa quá nhỏ lại thiếu cân đối. May đo giúp tối ưu kích thước theo chiều dài tường, lối đi và nhu cầu ngồi nghỉ.",
    },
    sectionTwo: {
      heading: "Chọn chất liệu theo thói quen sử dụng",
      body:
        "Gia đình có trẻ nhỏ hoặc nuôi thú cưng thường cần chất liệu dễ vệ sinh. Không gian sang trọng có thể ưu tiên màu vải, độ mềm và form dáng tinh tế hơn.",
    },
    faq: {
      question: "Sofa may đo mất bao lâu?",
      answer:
        "Thời gian phụ thuộc mẫu, chất liệu và lịch sản xuất. Bạn nên liên hệ Cưng Home để được tư vấn thời gian cụ thể theo mẫu chọn.",
    },
  }),
  "ghe-sofa": makePage({
    key: "ghe-sofa",
    path: "/ghe-sofa",
    keyword: "ghế sofa",
    title: "Ghế Sofa Bọc Nệm Cho Phòng Khách - Cưng Home",
    description:
      "Cưng Home tư vấn ghế sofa, sofa băng, ghế đơn, đôn sofa và các mẫu ghế bọc nệm phù hợp phòng khách gia đình.",
    h1: "Ghế sofa bọc nệm cho phòng khách",
    intro:
      "Ghế sofa là điểm chính của phòng khách, cần đủ êm, đúng kích thước và dễ phối với màu tường, sàn nhà, bàn trà.",
    highlights: [
      "Có ghế sofa băng, ghế đơn, sofa chữ L và đôn phối bộ.",
      "Tùy chọn chất liệu bọc nệm và màu vải.",
      "Tư vấn theo diện tích phòng và số người sử dụng.",
    ],
    sectionOne: {
      heading: "Ghế sofa cần phù hợp tần suất sử dụng",
      body:
        "Nếu sofa dùng hằng ngày, độ bền khung, độ nâng đỡ nệm và chất liệu bọc rất quan trọng. Cưng Home tư vấn theo nhu cầu thực tế của gia đình.",
    },
    sectionTwo: {
      heading: "Phối ghế sofa với không gian",
      body:
        "Màu ghế sofa nên hài hòa với sàn, rèm, tường và ánh sáng. Những màu trung tính thường dễ dùng lâu dài, trong khi màu nổi bật tạo điểm nhấn mạnh hơn.",
    },
    faq: {
      question: "Ghế sofa khác sofa bộ như thế nào?",
      answer:
        "Ghế sofa có thể là một mẫu đơn lẻ hoặc một phần trong bộ sofa. Tùy không gian, khách có thể chọn sofa băng, sofa chữ L, ghế đơn hoặc đôn phối thêm.",
    },
  }),
  "giuong-boc-nem": makePage({
    key: "giuong-boc-nem",
    path: "/giuong-boc-nem",
    keyword: "giường bọc nệm",
    title: "Giường Bọc Nệm Êm Ái Theo Kích Thước - Cưng Home",
    description:
      "Cưng Home tư vấn giường bọc nệm, giường vải, giường phòng ngủ với màu vải, kích thước và kiểu dáng theo nhu cầu.",
    h1: "Giường bọc nệm cho phòng ngủ ấm áp",
    intro:
      "Ngoài sofa, Cưng Home còn tư vấn giường bọc nệm cho phòng ngủ cần cảm giác êm, mềm và đồng bộ với nội thất gia đình.",
    productHref: "/?type=Giường#products",
    highlights: [
      "Tư vấn kích thước giường theo phòng ngủ.",
      "Chọn màu vải, chất liệu và kiểu đầu giường.",
      "Phù hợp căn hộ, nhà phố và phòng ngủ gia đình.",
    ],
    sectionOne: {
      heading: "Giường bọc nệm tạo cảm giác mềm mại hơn",
      body:
        "So với các kiểu giường cứng, giường bọc nệm mang lại cảm giác ấm áp và dễ phối với rèm, thảm, tủ đầu giường trong không gian phòng ngủ.",
    },
    sectionTwo: {
      heading: "Chọn kích thước theo lối đi và diện tích phòng",
      body:
        "Khi chọn giường, cần tính cả khoảng mở tủ, lối đi hai bên và vị trí ổ điện. Cưng Home tư vấn để giường vừa đẹp vừa thuận tiện sử dụng.",
    },
    faq: {
      question: "Giường bọc nệm có chọn màu vải được không?",
      answer:
        "Có. Khách có thể trao đổi màu vải, chất liệu và kiểu dáng đầu giường để phù hợp phong cách phòng ngủ.",
    },
  }),
  "noi-that-phong-khach": makePage({
    key: "noi-that-phong-khach",
    path: "/noi-that-phong-khach",
    keyword: "nội thất phòng khách",
    title: "Nội Thất Phòng Khách: Sofa, Ghế, Đôn - Cưng Home",
    description:
      "Cưng Home tư vấn nội thất phòng khách với sofa, ghế bọc nệm, đôn sofa, sofa chữ L và sofa bed theo không gian thực tế.",
    h1: "Nội thất phòng khách xoay quanh bộ sofa phù hợp",
    intro:
      "Một phòng khách đẹp bắt đầu từ bộ sofa đúng kích thước, đúng chất liệu và hài hòa với bàn trà, thảm, ánh sáng và thói quen sinh hoạt.",
    highlights: [
      "Tư vấn sofa và ghế bọc nệm theo bố cục phòng khách.",
      "Gợi ý phối màu vải với sàn, rèm, tường và bàn trà.",
      "Phù hợp căn hộ, nhà phố, phòng khách nhỏ và không gian mở.",
    ],
    sectionOne: {
      heading: "Sofa là điểm neo của phòng khách",
      body:
        "Bộ sofa thường quyết định cảm giác chính của phòng khách. Khi chọn đúng kích thước và màu sắc, các món còn lại như bàn trà, thảm và đèn sẽ dễ phối hơn.",
    },
    sectionTwo: {
      heading: "Tư vấn theo không gian thật",
      body:
        "Cưng Home khuyến khích khách gửi ảnh phòng hoặc kích thước cơ bản để tư vấn kiểu sofa, màu vải và bố cục phù hợp hơn thay vì chọn theo ảnh mẫu đơn lẻ.",
    },
    faq: {
      question: "Nội thất phòng khách nên bắt đầu từ món nào?",
      answer:
        "Thông thường nên bắt đầu từ sofa vì đây là món lớn nhất và ảnh hưởng nhiều nhất tới bố cục, màu sắc và cảm giác sử dụng của phòng khách.",
    },
  }),
  "sofa-gan-day": makePage({
    key: "sofa-gan-day",
    path: "/sofa-gan-day",
    keyword: "sofa gần đây",
    title: "Sofa Gần Đây Tại Hồ Chí Minh - Cưng Home",
    description:
      "Tìm sofa gần đây tại Hồ Chí Minh? Cưng Home tư vấn sofa, sofa chữ L, sofa bed, giường bọc nệm, giao hàng và lắp đặt tại nhà.",
    h1: "Tìm sofa gần đây tại Hồ Chí Minh",
    intro:
      "Nếu bạn đang tìm nơi tư vấn sofa gần đây, Cưng Home hỗ trợ chọn mẫu online, tư vấn kích thước theo phòng khách và giao lắp tại nhà ở khu vực Hồ Chí Minh.",
    highlights: [
      `${BUSINESS_INFO.officeLabel}: ${BUSINESS_INFO.addressShort}.`,
      `${BUSINESS_INFO.workshopLabel}: ${BUSINESS_INFO.workshopAreaShort}.`,
      "Tư vấn nhanh qua Zalo, Messenger hoặc điện thoại.",
      "Giao hàng, lắp đặt và hỗ trợ chọn mẫu theo không gian thực tế.",
    ],
    sectionOne: {
      heading: "Phù hợp nhu cầu tìm kiếm gần bạn",
      body:
        "Các tìm kiếm như sofa gần đây, cửa hàng sofa gần đây hoặc xưởng sofa gần đây thường cần thông tin khu vực rõ ràng. Cưng Home có văn phòng tư vấn tại S501 Vinhomes Grand Park, khu vực xưởng ở Long Bình gần Vinhomes Grand Park khoảng 1km và các kênh liên hệ trực tiếp để khách dễ trao đổi mẫu, kích thước.",
    },
    sectionTwo: {
      heading: "Tư vấn trước khi đến xem mẫu",
      body:
        "Bạn có thể gửi ảnh phòng khách, kích thước tường và phong cách mong muốn. Cưng Home sẽ gợi ý dòng sofa phù hợp trước khi hẹn xem mẫu hoặc chốt phương án sản xuất.",
    },
    faq: {
      question: "Cưng Home ở khu vực nào?",
      answer:
        `Văn phòng tư vấn Cưng Home ở ${BUSINESS_INFO.addressShort}. Khu vực xưởng ở ${BUSINESS_INFO.workshopAreaShort}. Khách có thể liên hệ trước qua Zalo, Messenger hoặc điện thoại để được tư vấn nhanh.`,
    },
  }),
};

export const seoLandingPaths = Object.values(SEO_LANDING_PAGES).map((page) => page.path);

export const seoLandingLinks = Object.values(SEO_LANDING_PAGES).map((page) => ({
  title: page.keyword,
  description: page.description,
  href: page.path,
}));

export default function SeoLandingPage({ pageKey }) {
  const page = SEO_LANDING_PAGES[pageKey] || SEO_LANDING_PAGES.sofa;

  useEffect(() => {
    const title = page.title.replace(/\s+-\s+Cưng Home$/u, ` - ${BUSINESS_INFO.name}`);
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faqs.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    };

    setSeo({
      title,
      description: page.description,
      canonicalPath: page.path,
      image: "/logo.jpg",
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: page.h1,
          description: page.description,
          url: `${SITE_URL}${page.path}`,
          keywords: page.keyword,
          publisher: {
            "@type": "FurnitureStore",
            "@id": `${SITE_URL}/#store`,
            name: BUSINESS_INFO.name,
            url: SITE_URL,
            telephone: BUSINESS_INFO.telephoneSchema,
            address: {
              "@type": "PostalAddress",
              ...BUSINESS_INFO.address,
            },
            areaServed: BUSINESS_INFO.areaServed.map((name) => ({
              "@type": "Place",
              name,
            })),
          },
        },
        faqSchema,
      ],
    });
  }, [page]);

  const localPriority = [
    "/sofa-quan-9",
    "/sofa-thu-duc",
    "/sofa-vinhomes-grand-park",
    "/xuong-sofa-tphcm",
    "/sofa-may-do",
    "/sofa-phong-khach",
  ];
  const relatedPages = Object.values(SEO_LANDING_PAGES)
    .filter((item) => item.path !== page.path)
    .sort((left, right) => {
      const leftIndex = localPriority.indexOf(left.path);
      const rightIndex = localPriority.indexOf(right.path);
      return (leftIndex === -1 ? 999 : leftIndex) - (rightIndex === -1 ? 999 : rightIndex);
    })
    .slice(0, 8);

  return (
    <div className="min-h-screen overflow-x-hidden">
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <Link
            to="/"
            aria-label="Về trang chủ Cưng Home"
            className="flex min-h-11 items-center"
          >
            <img
              src={logo}
              alt="Cưng Home"
              className="h-10 w-28 object-contain sm:w-32"
            />
          </Link>
          <a
            href={BUSINESS_INFO.phoneHref}
            onClick={() => trackContactClick("Hotline SEO landing header", { page_key: page.key })}
            className="inline-flex items-center gap-2 rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white"
          >
            <Phone className="w-4 h-4" />
            {BUSINESS_INFO.phone}
          </a>
        </div>
      </header>

      <main>
        <section className="relative flex min-h-[520px] items-center overflow-hidden pt-28 md:min-h-[560px] md:pt-32">
          <img
            src={banner}
            alt={page.h1}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative mx-auto w-full max-w-6xl px-4 py-14 text-white sm:px-6 md:py-16 lg:px-8">
            <p className="mb-3 text-sm font-semibold uppercase !text-white/90">Cưng Home</p>
            <h1 className="max-w-4xl break-words text-4xl font-bold leading-[1.08] !text-white sm:text-5xl lg:text-6xl">
              {page.h1}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-white/90 md:text-lg md:leading-8">
              {page.intro}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to={page.productHref}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-5 py-3 font-semibold text-[var(--color-primary)]"
              >
                {page.primaryCta}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={BUSINESS_INFO.messengerUrl}
                onClick={() => trackContactClick("Messenger SEO landing hero", { page_key: page.key })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-white/70 px-5 py-3 font-semibold text-white"
              >
                <MessageCircle className="w-4 h-4" />
                Chat tư vấn
              </a>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid min-w-0 gap-4 md:grid-cols-3">
            {page.highlights.map((item) => (
              <div key={item} className="min-w-0 rounded-lg border border-gray-100 bg-white p-5 shadow-sm">
                <p className="font-medium leading-7">{item}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white">
          <div className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-[1fr_0.85fr] gap-10">
            <div className="space-y-8">
              {page.sections.map((section) => (
                <article key={section.heading}>
                  <h2 className="text-2xl font-bold text-[var(--color-primary)]">
                    {section.heading}
                  </h2>
                  <p className="mt-3 leading-8 text-gray-700">{section.body}</p>
                </article>
              ))}
            </div>
            <aside className="rounded-lg border border-gray-100 bg-gray-50 p-6">
              <h2 className="text-xl font-bold text-gray-900">Thông tin liên hệ</h2>
              <p className="mt-4 flex items-start gap-2 text-gray-700">
                <MapPin className="w-5 h-5 mt-1 text-[var(--color-primary)]" />
                <span>
                  <span className="block font-semibold text-gray-900">
                    {BUSINESS_INFO.officeLabel}
                  </span>
                  {BUSINESS_INFO.addressShort}
                </span>
              </p>
              <p className="mt-3 flex items-start gap-2 text-gray-700">
                <MapPin className="w-5 h-5 mt-1 text-[var(--color-primary)]" />
                <span>
                  <span className="block font-semibold text-gray-900">
                    {BUSINESS_INFO.workshopLabel}
                  </span>
                  {BUSINESS_INFO.workshopAreaShort}
                </span>
              </p>
              <p className="mt-3 flex items-center gap-2 text-gray-700">
                <Phone className="w-5 h-5 text-[var(--color-primary)]" />
                {BUSINESS_INFO.phone}
              </p>
              <a
                href={BUSINESS_INFO.zaloUrl}
                onClick={() => trackContactClick("Zalo SEO landing", { page_key: page.key })}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex w-full items-center justify-center rounded-md bg-[var(--color-primary)] px-5 py-3 font-semibold text-white"
              >
                Nhắn Zalo tư vấn
              </a>
            </aside>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-[var(--color-primary)]">
            Câu hỏi thường gặp
          </h2>
          <div className="mt-6 grid gap-4">
            {page.faqs.map((item) => (
              <article key={item.question} className="rounded-lg bg-white border border-gray-100 p-5 shadow-sm">
                <h3 className="font-semibold text-gray-900">{item.question}</h3>
                <p className="mt-2 leading-7 text-gray-700">{item.answer}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="bg-white">
          <div className="max-w-6xl mx-auto px-4 py-12">
            <h2 className="text-2xl font-bold text-[var(--color-primary)]">
              Tìm kiếm liên quan
            </h2>
            <div className="mt-6 flex flex-wrap gap-3">
              {relatedPages.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="rounded-md border border-gray-200 bg-gray-50 px-4 py-2 font-medium text-gray-700 hover:text-[var(--color-primary)]"
                >
                  {item.keyword}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <ScrollControls />
      <Footer />
    </div>
  );
}
